import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import useSWR, { mutate } from "swr";
import client, { fetcher } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, Play, Copy, ShieldCheck, FileText } from "lucide-react";
import ApiExposicion from "@/pages/ApiExposicion";
import TextosInstitucionales from "@/pages/TextosInstitucionales";
import "@/i18n/fase5";

// URL alias -> internal tab value. Accepts both language variants for the users tab.
const TAB_ALIAS = {
  usuarios: "users",
  users: "users",
  keys: "keys",
  "api-keys": "keys",
  config: "config",
  configuracion: "config",
  tasks: "tasks",
  tareas: "tasks",
  tarefas: "tasks",
  "api-exposicion": "api-exposicion",
  "api-exposure": "api-exposicion",
  "api-publica": "api-exposicion",
  "exposicion-api": "api-exposicion",
  textos: "textos",
  "textos-institucionales": "textos",
  "textos-institucionais": "textos",
  memoria: "textos",
};
const VALID_TABS = ["users", "keys", "config", "tasks", "api-exposicion", "textos"];

export default function Admin() {
  const { t } = useTranslation();
  const { tab } = useParams();
  const navigate = useNavigate();
  const active = VALID_TABS.includes(TAB_ALIAS[tab] || tab) ? (TAB_ALIAS[tab] || tab) : "users";

  const onTabChange = (v) => {
    // reflect tab in URL for deep-linking (e.g. /app/admin/usuarios)
    const alias = v === "users" ? "usuarios" : v;
    navigate(`/app/admin/${alias}`, { replace: true });
  };

  return (
    <div data-testid="admin-page">
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-[0.24em] text-salnes-stone">{t("fase5.sidebar_admin")}</div>
        <h1 className="font-serif text-3xl text-salnes-ink">{t("fase5.adm_title")}</h1>
        <div className="text-sm text-salnes-stone mt-1">{t("fase5.adm_subtitle")}</div>
      </div>

      <Tabs value={active} onValueChange={onTabChange} className="w-full">
        <TabsList className="mb-6 bg-white border hairline rounded-full h-11 p-1">
          <TabsTrigger value="users" data-testid="adm-tab-users" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">{t("fase5.adm_tab_users")}</TabsTrigger>
          <TabsTrigger value="keys" data-testid="adm-tab-keys" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">{t("fase5.adm_tab_keys")}</TabsTrigger>
          <TabsTrigger value="config" data-testid="adm-tab-config" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">{t("fase5.adm_tab_config")}</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="adm-tab-tasks" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">{t("fase5.adm_tab_tasks")}</TabsTrigger>
          <TabsTrigger value="api-exposicion" data-testid="adm-tab-api-exposicion" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">
            <ShieldCheck size={13} className="mr-1.5" />
            {t("fase5.adm_tab_api")}
          </TabsTrigger>
          <TabsTrigger value="textos" data-testid="adm-tab-textos" className="rounded-full data-[state=active]:bg-salnes-atlantic data-[state=active]:text-white">
            <FileText size={13} className="mr-1.5" />
            {t("fase5.adm_tab_textos")}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersPanel /></TabsContent>
        <TabsContent value="keys"><KeysPanel /></TabsContent>
        <TabsContent value="config"><ConfigPanel /></TabsContent>
        <TabsContent value="tasks"><TasksPanel /></TabsContent>
        <TabsContent value="api-exposicion"><ApiExposicion /></TabsContent>
        <TabsContent value="textos"><TextosInstitucionales /></TabsContent>
      </Tabs>
    </div>
  );
}

function UsersPanel() {
  const { t } = useTranslation();
  const { data } = useSWR("/admin/users", fetcher);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "MANCOMUNIDADE", password: "" });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await client.post("/admin/users", form);
      toast.success(t("fase5.adm_user_created"));
      setOpen(false); setForm({ email: "", name: "", role: "MANCOMUNIDADE", password: "" });
      mutate("/admin/users");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("fase5.adm_user_error"));
    } finally { setSaving(false); }
  };

  const deactivate = async (email) => {
    try {
      await client.delete(`/admin/users/${email}`);
      toast.success(t("fase5.adm_user_deactivated"));
      mutate("/admin/users");
    } catch (e) { toast.error(e?.response?.data?.detail || t("fase5.adm_user_error")); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setOpen(true)} data-testid="adm-user-new-btn" className="rounded-full bg-salnes-atlantic hover:bg-salnes-atlantic/90">
          <Plus size={13} className="mr-2" /> {t("fase5.adm_user_new")}
        </Button>
      </div>
      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="adm-users-table">
          <thead className="bg-salnes-sand/40 text-salnes-stone text-[10px] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left px-4 py-3">{t("fase5.adm_user_email")}</th>
              <th className="text-left px-4 py-3">{t("fase5.adm_user_name")}</th>
              <th className="text-left px-4 py-3">{t("fase5.adm_user_role")}</th>
              <th className="text-left px-4 py-3">{t("fase5.adm_user_active")}</th>
              <th className="text-right px-4 py-3">{t("fase5.adm_user_actions")}</th>
            </tr>
          </thead>
          <tbody className="divide-y hairline">
            {(data?.users || []).map((u) => (
              <tr key={u.email} data-testid={`adm-user-row-${u.email}`}>
                <td className="px-4 py-3 text-salnes-ink">{u.email}</td>
                <td className="px-4 py-3">{u.name}</td>
                <td className="px-4 py-3"><Badge variant="outline" className="text-[10px]">{u.role}</Badge></td>
                <td className="px-4 py-3">{u.active ? <span className="text-salnes-emerald">●</span> : <span className="text-salnes-coral">●</span>}</td>
                <td className="px-4 py-3 text-right">
                  {u.active && (
                    <Button size="sm" variant="ghost" onClick={() => deactivate(u.email)} data-testid={`adm-user-deactivate-${u.email}`}>
                      <Trash2 size={12} />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
            {data && (data.users || []).length === 0 && (
              <tr data-testid="adm-users-empty">
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-salnes-stone italic">
                  {t("fase5.adm_users_empty")}
                </td>
              </tr>
            )}
            {!data && (
              <tr data-testid="adm-users-loading">
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-salnes-stone italic">
                  {t("fase5.adm_loading")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="adm-user-dialog">
          <DialogHeader><DialogTitle>{t("fase5.adm_user_new")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder={t("fase5.adm_user_email")} type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="adm-user-form-email" />
            <Input placeholder={t("fase5.adm_user_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="adm-user-form-name" />
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="adm-user-form-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ADMIN">ADMIN</SelectItem>
                <SelectItem value="SUPERVISOR">SUPERVISOR</SelectItem>
                <SelectItem value="MANCOMUNIDADE">MANCOMUNIDADE</SelectItem>
              </SelectContent>
            </Select>
            <Input placeholder={t("fase5.adm_user_password")} type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="adm-user-form-password" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>{t("fase5.adm_cancel")}</Button>
            <Button onClick={save} disabled={saving || !form.email || !form.password} data-testid="adm-user-form-save" className="bg-salnes-atlantic hover:bg-salnes-atlantic/90">
              {saving ? <Loader2 size={13} className="mr-2 animate-spin" /> : null}
              {t("fase5.adm_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function KeysPanel() {
  const { t } = useTranslation();
  const { data } = useSWR("/admin/api-keys", fetcher);
  const [open, setOpen] = useState(false);
  const [issued, setIssued] = useState(null);
  const [form, setForm] = useState({ name: "", owner_email: "" });
  const [saving, setSaving] = useState(false);

  const issue = async () => {
    setSaving(true);
    try {
      const { data: r } = await client.post("/admin/api-keys", form);
      setIssued(r);
      setForm({ name: "", owner_email: "" });
      mutate("/admin/api-keys");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const revoke = async (id) => {
    try {
      await client.delete(`/admin/api-keys/${id}`);
      mutate("/admin/api-keys");
    } catch { toast.error("Error"); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={() => setOpen(true)} data-testid="adm-key-new-btn" className="rounded-full bg-salnes-atlantic hover:bg-salnes-atlantic/90">
          <Plus size={13} className="mr-2" /> {t("fase5.adm_key_new")}
        </Button>
      </div>
      <div className="rounded-2xl border hairline bg-white overflow-hidden">
        <table className="w-full text-sm" data-testid="adm-keys-table">
          <thead className="bg-salnes-sand/40 text-salnes-stone text-[10px] uppercase tracking-[0.18em]">
            <tr>
              <th className="text-left px-4 py-3">{t("fase5.adm_key_name")}</th>
              <th className="text-left px-4 py-3">{t("fase5.adm_key_owner")}</th>
              <th className="text-left px-4 py-3">{t("fase5.adm_key_created")}</th>
              <th className="text-right px-4 py-3">{t("fase5.adm_key_requests")}</th>
              <th className="text-left px-4 py-3">{t("fase5.adm_key_active")}</th>
              <th className="text-right px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y hairline">
            {(data?.keys || []).map((k) => (
              <tr key={k.id} data-testid={`adm-key-row-${k.id}`}>
                <td className="px-4 py-3">{k.name}</td>
                <td className="px-4 py-3 text-[12px]">{k.owner_email}</td>
                <td className="px-4 py-3 text-[12px]">{k.created_at?.slice(0, 10)}</td>
                <td className="px-4 py-3 text-right tabular-nums">{k.requests_count || 0}</td>
                <td className="px-4 py-3">{k.active ? <span className="text-salnes-emerald">●</span> : <span className="text-salnes-stone/50">●</span>}</td>
                <td className="px-4 py-3 text-right">
                  {k.active && <Button size="sm" variant="ghost" onClick={() => revoke(k.id)} data-testid={`adm-key-revoke-${k.id}`}><Trash2 size={12} /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setIssued(null); }}>
        <DialogContent data-testid="adm-key-dialog">
          <DialogHeader><DialogTitle>{t("fase5.adm_key_new")}</DialogTitle></DialogHeader>
          {!issued ? (
            <>
              <div className="space-y-3">
                <Input placeholder={t("fase5.adm_key_name")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="adm-key-form-name" />
                <Input placeholder={t("fase5.adm_key_owner")} type="email" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} data-testid="adm-key-form-email" />
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>{t("fase5.adm_cancel")}</Button>
                <Button onClick={issue} disabled={saving || !form.name || !form.owner_email} data-testid="adm-key-form-issue" className="bg-salnes-atlantic hover:bg-salnes-atlantic/90">
                  {saving ? <Loader2 size={13} className="mr-2 animate-spin" /> : null}
                  {t("fase5.adm_confirm")}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-salnes-coral font-medium">{t("fase5.adm_key_copy_warning")}</div>
              <div className="rounded-lg bg-salnes-ink text-salnes-ivory p-3 text-[11px] font-mono break-all" data-testid="adm-key-issued-value">{issued.key}</div>
              <Button size="sm" onClick={() => { navigator.clipboard.writeText(issued.key); toast.success(t("fase5.adm_key_copied")); }} data-testid="adm-key-copy" variant="outline" className="rounded-full">
                <Copy size={12} className="mr-2" /> Copiar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ConfigPanel() {
  const { t } = useTranslation();
  const { data } = useSWR("/admin/settings", fetcher);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const current = { ...(data || {}), ...form };
  const set = (k, v) => setForm({ ...form, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      await client.patch("/admin/settings", form);
      toast.success(t("fase5.adm_config_saved"));
      setForm({});
      mutate("/admin/settings");
    } catch { toast.error("Error"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="text-sm text-salnes-stone">Loading…</div>;
  return (
    <div className="rounded-2xl border hairline bg-white p-6 max-w-2xl" data-testid="adm-config-panel">
      <div className="space-y-4">
        <Field label={t("fase5.adm_config_baseline")}><Input type="number" value={current.baseline_year || ""} onChange={(e) => set("baseline_year", parseInt(e.target.value))} data-testid="adm-config-baseline" /></Field>
        <Field label={t("fase5.adm_config_consumption")}><Input type="number" step="0.01" value={current.avg_consumption_l_per_km || ""} onChange={(e) => set("avg_consumption_l_per_km", parseFloat(e.target.value))} data-testid="adm-config-consumption" /></Field>
        <Field label={t("fase5.adm_config_contact_email")}><Input type="email" value={current.contact_email || ""} onChange={(e) => set("contact_email", e.target.value)} data-testid="adm-config-email" /></Field>
        <Field label={t("fase5.adm_config_contact_phone")}><Input value={current.contact_phone || ""} onChange={(e) => set("contact_phone", e.target.value)} data-testid="adm-config-phone" /></Field>
        <Field label={t("fase5.adm_config_name_gl")}><Input value={current.mancomunidade_full_name_gl || ""} onChange={(e) => set("mancomunidade_full_name_gl", e.target.value)} data-testid="adm-config-name-gl" /></Field>
        <Field label={t("fase5.adm_config_name_es")}><Input value={current.mancomunidade_full_name_es || ""} onChange={(e) => set("mancomunidade_full_name_es", e.target.value)} data-testid="adm-config-name-es" /></Field>
      </div>
      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving || Object.keys(form).length === 0} data-testid="adm-config-save" className="rounded-full bg-salnes-atlantic hover:bg-salnes-atlantic/90">
          {saving ? <Loader2 size={13} className="mr-2 animate-spin" /> : null}
          {t("fase5.adm_config_save")}
        </Button>
      </div>
    </div>
  );
}

function TasksPanel() {
  const { t } = useTranslation();
  const { data } = useSWR("/admin/tasks/status", fetcher);
  const [running, setRunning] = useState(null);
  const [lastResult, setLastResult] = useState({});

  const TASKS = [
    { name: "sync_detections", label: "Sync detections (TrisTrash)" },
    { name: "sync_vehicles", label: "Sync vehicles (TrisTrash)" },
    { name: "kpi_rollup", label: "KPI rollup" },
    { name: "sources_health", label: "Data sources health probe" },
    { name: "carbon_rollup", label: "Carbon calculations rollup" },
  ];

  const run = async (name) => {
    setRunning(name);
    try {
      const { data: r } = await client.post(`/admin/tasks/${name}/run`);
      setLastResult((prev) => ({ ...prev, [name]: r.result }));
      toast.success(t("fase5.adm_task_ok"));
      mutate("/admin/tasks/status");
    } catch { toast.error("Error"); }
    finally { setRunning(null); }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-salnes-stone italic">{t("fase5.adm_tasks_intro")}</div>
      {TASKS.map((t2) => (
        <div key={t2.name} className="rounded-2xl border hairline bg-white p-5 flex items-center justify-between" data-testid={`adm-task-${t2.name}`}>
          <div>
            <div className="font-medium text-salnes-ink">{t2.label}</div>
            <div className="text-[11px] text-salnes-stone font-mono">{t2.name}</div>
            {lastResult[t2.name] && (
              <div className="text-[10px] text-salnes-stone mt-1 font-mono">{JSON.stringify(lastResult[t2.name]).slice(0, 100)}</div>
            )}
          </div>
          <Button onClick={() => run(t2.name)} disabled={running === t2.name} data-testid={`adm-task-run-${t2.name}`} className="rounded-full bg-salnes-atlantic hover:bg-salnes-atlantic/90">
            {running === t2.name ? <Loader2 size={13} className="mr-2 animate-spin" /> : <Play size={13} className="mr-2" />}
            {running === t2.name ? t("fase5.adm_task_running") : t("fase5.adm_task_run")}
          </Button>
        </div>
      ))}
      {data && (
        <div className="mt-6 rounded-2xl border hairline bg-white p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-salnes-stone mb-2">{t("fase5.adm_task_last")}</div>
          <pre className="text-[10px] font-mono text-salnes-ink/80 overflow-auto">{JSON.stringify(data, null, 2)}</pre>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.2em] text-salnes-stone mb-1">{label}</div>
    {children}
  </div>
);
